import { isMobile } from "../constants/dimension";
import { isAndroidApp } from "./platform";

type IntercomCommand = (command: string, ...args: unknown[]) => void;

type IntercomStub = IntercomCommand & {
  q: unknown[][];
  c?: (args: unknown[]) => void;
};

const INTERCOM_SCRIPT_ID = "intercom-widget";

const INTERCOM_APP_ID = "olvwt9qv";

const INTERCOM_SCRIPT_URL = `https://widget.intercom.io/widget/${INTERCOM_APP_ID}`;

const INTERCOM_KEYBOARD_STYLE_ID = "intercom-keyboard-inset";
const INTERCOM_KEYBOARD_INSET_VAR = "--intercom-keyboard-inset";

const MOBILE_WEB_KEYBOARD_CSS = `
  .intercom-messenger-frame {
    bottom: var(${INTERCOM_KEYBOARD_INSET_VAR}, 0px) !important;
    height: calc(100% - var(${INTERCOM_KEYBOARD_INSET_VAR}, 0px)) !important;
  }
`;

declare global {
  interface Window {
    Intercom: (command: string, ...args: unknown[]) => void;
    intercomSettings: {
      app_id: string;
      hide_default_launcher?: boolean;
      user_id?: string;
      email?: string | null;
      name?: string | null;
      language_override?: string | null;
      intercom_user_jwt?: string;
      [key: string]: unknown;
    };
  }
}

function injectStyle(id: string, css: string): void {
  if (document.getElementById(id)) return;
  const style = document.createElement("style");
  style.id = id;
  style.textContent = css;
  document.head.appendChild(style);
}

export const loadIntercom = () => {
  if (document.getElementById(INTERCOM_SCRIPT_ID)) return;

  if (isMobile) {
    injectStyle(INTERCOM_KEYBOARD_STYLE_ID, MOBILE_WEB_KEYBOARD_CSS);
  }

  window.intercomSettings = {
    app_id: INTERCOM_APP_ID,
    hide_default_launcher: true,
  };

  if (typeof window.Intercom !== "function") {
    const queue = function (...args: unknown[]) {
      queue.q.push(args);
    } as IntercomStub;
    queue.q = [];
    queue.c = (args) => queue.q.push(args);
    window.Intercom = queue as typeof window.Intercom;
  }

  const script = document.createElement("script");
  script.async = true;
  script.id = INTERCOM_SCRIPT_ID;
  script.src = INTERCOM_SCRIPT_URL;
  script.addEventListener("load", () => {
    window.Intercom("shutdown");
    window.Intercom("boot", window.intercomSettings);
  });

  const firstScript = document.getElementsByTagName("script")[0];
  firstScript.parentNode?.insertBefore(script, firstScript);
};

let bootedJwt: string | undefined;

export const updateIntercom = (attrs: Record<string, unknown>): boolean => {
  const jwt = attrs.intercom_user_jwt as string | undefined;

  window.intercomSettings = { ...window.intercomSettings, ...attrs };

  if (!jwt || jwt === bootedJwt) {
    window.Intercom("update", attrs);
    return false;
  }

  bootedJwt = jwt;
  window.Intercom("shutdown");
  window.Intercom("boot", window.intercomSettings);
  return true;
};

export function trackIntercomKeyboardInset(): () => void {
  const viewport = window.visualViewport;
  if (!isMobile || isAndroidApp || !viewport) return () => {};

  const root = document.documentElement;
  const update = () => {
    const inset = document.fullscreenElement
      ? Math.max(0, window.innerHeight - viewport.height - viewport.offsetTop)
      : 0;
    root.style.setProperty(INTERCOM_KEYBOARD_INSET_VAR, `${inset}px`);
  };

  update();
  viewport.addEventListener("resize", update);
  viewport.addEventListener("scroll", update);
  document.addEventListener("fullscreenchange", update);

  return () => {
    viewport.removeEventListener("resize", update);
    viewport.removeEventListener("scroll", update);
    document.removeEventListener("fullscreenchange", update);
    root.style.removeProperty(INTERCOM_KEYBOARD_INSET_VAR);
  };
}
